import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Target,
  CalendarCheck,
  Send,
  MessageSquare,
  MessagesSquare,
  Search,
  Settings,
  LifeBuoy,
  MessageCircle,
  ChevronDown,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { ContactForm } from "../components/ContactForm";
import { SectionHeader, StatBlock, ProcessCard } from "../components/Shared";
import { SeoText } from "../components/SeoText";
import { WHATSAPP_LINK } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { useState } from "react";

const heroStats = [
  { value: "3–8h", label: "Gagnées par semaine" },
  { value: "×10", label: "Plus rapide qu'un humain" },
  { value: "24/7", label: "Disponibilité" },
  { value: "0", label: "Tâche oubliée" },
];

const vocalStats = [
  { value: "30%", label: "Opportunités récupérées" },
  { value: "0", label: "Appel manqué" },
  { value: "48h", label: "Mise en place" },
  { value: "24/7", label: "Disponibilité" },
];

const wppStats = [
  { value: "24/7", label: "Disponible" },
  { value: "<30s", label: "Temps de réponse" },
  { value: "0", label: "Application à installer" },
  { value: "100%", label: "Via WhatsApp" },
];

const services = [
  { num: "01", title: "Audit IA", price: "Gratuit", desc: "Vous ne savez pas par où commencer avec l'IA.", to: "/audit-ia" },
  { num: "02", title: "Agent WhatsApp", price: "", desc: "Vos équipes répondent aux mêmes questions 20x/jour.", to: "/services/whatsapp-ia" },
  { num: "03", title: "Réceptionniste Vocal IA", price: "", desc: "Appels manqués, secrétariat coûteux.", to: "/services/receptionniste-ia" },
  { num: "04", title: "Intégration Systèmes", price: "", desc: "Vos outils ne se parlent pas." },
  { num: "05", title: "Pipeline Email IA", price: "", desc: "Des heures perdues sur les emails chaque semaine." },
  { num: "06", title: "Agent Sur Mesure", price: "", desc: "Un processus spécifique qui coûte cher en temps." },
];

const faqs = [
  { q: "Est-ce compliqué à mettre en place ?", a: "Non. Je configure tout sur vos outils existants. Vous n'avez rien à installer." },
  { q: "Combien de temps avant de voir les résultats ?", a: "Les premiers résultats sont visibles dès la première semaine de déploiement." },
  { q: "Et si ça ne fonctionne pas pour mon activité ?", a: "C'est justement le rôle de l'audit gratuit de le vérifier avant tout engagement." },
  { q: "Est-ce que mes données sont sécurisées ?", a: "Hébergement suisse, conformité LPD/FADP. Vos données ne quittent pas la Suisse." },
  { q: "Quel est le coût ?", a: "L'audit initial est gratuit. Les tarifs des services sont communiqués sur demande, après l'audit, en fonction de votre situation." },
  { q: "Je ne suis pas tech — est-ce que c'est pour moi ?", a: "Oui. Mes clients les plus satisfaits ne savent pas coder. Vous n'avez rien à apprendre." },
];

const Home = () => {
  const [openFaq, setOpenFaq] = useState(0);
  useSeo({
    title: "Consultant IA Genève — Automatisation pour PME Suisse romande",
    description: "J'automatise les tâches répétitives de votre PME avec l'IA. Déjà déployé à Genève dans plusieurs secteurs. Résultats concrets, audit gratuit.",
    path: "/",
  });

  return (
    <Layout>
      {/* HERO — centered, screenshot style */}
      <section className="px-6 pt-16 pb-12 md:pt-28 md:pb-20 text-center">
        <div className="max-w-4xl mx-auto fade-in">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold title-text leading-[1.02] tracking-tight">
            Et si votre prochain
            <br />
            <span className="accent-text">employé IA</span>
            <br />
            ne prenait jamais de vacances ?
          </h1>
          <h2 className="text-base md:text-lg body-text mt-8 max-w-2xl mx-auto leading-relaxed font-normal">
            J'installe des systèmes d'automatisation IA dans les PME de Genève
            et Suisse romande.
          </h2>
          <div className="flex flex-wrap justify-center gap-2 mt-9">
            <span className="pill-badge">🇨🇭 Basé à Genève</span>
            <span className="pill-badge">🔒 Conforme LPD / FADP</span>
            <span className="pill-badge">⚡ Réponse sous 24h</span>
          </div>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Link to="/audit-ia" className="btn-primary" data-testid="hero-cta-audit">
              Réserver un audit gratuit de 60 min <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="hero-cta-contact">
              Prendre contact <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="px-6 pb-16">
        <div className="max-w-5xl mx-auto fade-in">
          <StatBlock stats={heroStats} testId="hero-stats" />
        </div>
      </section>

      {/* PRODUIT VEDETTE */}
      <FeaturedProduct
        badge="★ Produit vedette"
        title="Le Réceptionniste"
        accent="Vocal IA"
        desc="Une réceptionniste qui ne dort jamais. Répond à chaque appel, 24h/24, 7j/7, nuits, week-ends et jours fériés inclus."
        badges={[]}
        stats={vocalStats}
        link="/services/receptionniste-ia"
        process={[
          { num: "01", badge: "3 minutes", Icon: Phone, title: "Client appelle", text: "À 22h, le dimanche ou en plein été — l'IA décroche immédiatement." },
          { num: "02", badge: "Instantané", Icon: Target, title: "L'IA qualifie", text: "Besoin, budget, urgence — la conversation reste naturelle et fluide." },
          { num: "03", badge: "Immédiat", Icon: CalendarCheck, title: "RDV confirmé", text: "Créneau réservé en direct dans votre agenda. SMS + email envoyés." },
        ]}
        reality
      />

      {/* COUP DE CŒUR */}
      <FeaturedProduct
        badge="★ Coup de cœur"
        title="Votre assistant IA"
        accent="personnel sur WhatsApp."
        desc="Envoyez un message vocal ou texte — il s'occupe du reste."
        badges={[]}
        stats={wppStats}
        link="/services/whatsapp-ia"
        process={[
          { num: "01", badge: "Vocal ou texte", Icon: Send, title: "Envoyez un message", text: "Sur WhatsApp, comme à un collègue — vocal ou texte, c'est vous qui choisissez." },
          { num: "02", badge: "Instantané", Icon: MessageSquare, title: "L'IA comprend & exécute", text: "Elle interprète votre demande et lance l'action : email rédigé, RDV créé, info récupérée." },
          { num: "03", badge: "<30 secondes", Icon: MessagesSquare, title: "Vous recevez le résultat", text: "Réponse directement sur WhatsApp. Validez, ajustez ou envoyez en un tap." },
        ]}
        alt
      />

      {/* SERVICES */}
      <section id="services" className="px-6 py-20 scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Nos services" title="Six systèmes." accentTitle="Un seul objectif." subtitle="Vous faire gagner des heures chaque semaine." />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.num} className="card-base fade-in" data-testid={`service-card-${s.num}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl font-extrabold title-text opacity-20">{s.num}</span>
                  {s.price && (
                    <span className="pill-badge !text-[0.6rem] !py-1 !px-3">{s.price}</span>
                  )}
                </div>
                <h3 className="title-text text-lg font-bold mb-2">{s.title}</h3>
                <p className="body-text text-sm leading-relaxed">{s.desc}</p>
                {s.to && (
                  <Link to={s.to} className="mt-5 inline-flex items-center gap-1 accent-text text-sm font-semibold hover:gap-2 transition-all">
                    Découvrir <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MÉTHODE */}
      <section className="px-6 py-20 section-alt">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Méthode" title="Comment" accentTitle="ça marche ?" />
          <div className="grid md:grid-cols-3 gap-6">
            <ProcessCard num="01" badge="60 minutes" Icon={Search} title="Audit gratuit" text="On identifie ensemble les tâches répétitives qui vous coûtent du temps. 30 minutes. Gratuit. Sans engagement." />
            <ProcessCard num="02" badge="Sur vos outils" Icon={Settings} title="Configuration" text="Je configure l'automatisation sur vos outils existants. Pas besoin de changer vos habitudes." />
            <ProcessCard num="03" badge="WhatsApp direct" Icon={LifeBuoy} title="Vous n'êtes jamais seul" text="Entrepreneur moi-même, je sais ce que veut dire urgent. Je réponds sur WhatsApp — le soir, le week-end. Pas de ticket, pas d'attente." />
          </div>
        </div>
      </section>

      {/* À PROPOS */}
      <section className="px-6 py-20">
        <div className="max-w-4xl mx-auto fade-in">
          <span className="pill-badge mb-5">À propos</span>
          <h2 className="section-title mb-6">
            Construit par un entrepreneur. <span className="accent-text">Pas une agence.</span>
          </h2>
          <p className="title-text font-semibold text-lg leading-relaxed mb-4">
            Entrepreneur genevois depuis 12 ans. J'ai toujours cherché à faire plus avec moins — l'IA est simplement le meilleur outil que j'ai trouvé.
          </p>
          <p className="body-text leading-relaxed mb-6">
            J'ai automatisé mes propres sociétés avant de conseiller les vôtres. On parle chiffres, pas de jargon.
          </p>
          <div className="card-base border-l-4" style={{ borderLeftColor: "#3ecf8e" }}>
            <p className="accent-text italic text-lg font-semibold leading-relaxed">
              « En 2026, les PME qui n'automatisent pas travaillent pour celles qui le font. »
            </p>
          </div>
          <Link to="/a-propos" className="btn-secondary mt-8" data-testid="home-apropos-link">
            En savoir plus <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* SUR MESURE */}
      <section className="px-6 py-16">
        <div className="max-w-5xl mx-auto rounded-3xl p-10 md:p-16 fade-in" style={{ background: "linear-gradient(135deg,#3ecf8e 0%,#2db97a 100%)" }}>
          <span className="pill-badge-dark mb-6">Sur mesure</span>
          <h2 className="text-white font-extrabold text-3xl md:text-4xl leading-tight mb-3">
            Un besoin spécifique ?
          </h2>
          <p className="text-white/90 mb-8 text-lg">Si ça se répète, ça s'automatise.</p>
          <Link to="/contact" className="btn-outline-white" data-testid="sur-mesure-cta">
            Parlons de votre projet <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Témoignages" title="Ils ont" accentTitle="franchi le pas." />
          <div className="grid md:grid-cols-2 gap-6">
            <Testimonial quote="Mon assistant WhatsApp répond à mes clients à 23h. J'ai récupéré 6h par semaine." author="Marc T." role="Dirigeant, société de transport VIP" />
            <Testimonial quote="Le rapport quotidien automatique m'arrive chaque matin. Je n'ouvre plus Excel." author="Sophie R." role="Gérante, parking privé genevois" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-20 section-alt">
        <div className="max-w-3xl mx-auto">
          <SectionHeader badge="FAQ" title="Questions" accentTitle="fréquentes." />
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <button
                key={i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="card-base w-full text-left"
                data-testid={`faq-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="title-text font-semibold">{f.q}</span>
                  <ChevronDown
                    size={20}
                    className={`accent-text shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </div>
                {openFaq === i && (
                  <p className="body-text mt-4 text-sm leading-relaxed">{f.a}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="px-6 py-20 scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Démarrons" title="Prêt à déployer" accentTitle="votre premier agent IA ?" subtitle="Une conversation suffit." />
          <div className="grid md:grid-cols-2 gap-6">
            <ContactForm source="contact" page="/" />
            <div
              className="card-base !p-10 text-white flex flex-col justify-between"
              style={{ background: "linear-gradient(135deg,#3ecf8e 0%,#2db97a 100%)" }}
            >
              <div>
                <span className="pill-badge-dark mb-5">💬 Réponse immédiate</span>
                <h3 className="text-white font-extrabold text-3xl mb-3">
                  Discutons sur <span className="text-white">WhatsApp</span>
                </h3>
                <p className="text-white/90 mb-8 leading-relaxed">
                  Envoyez-moi un message — réponse immédiate.
                </p>
              </div>
              <div>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline-white"
                  data-testid="home-whatsapp-cta"
                >
                  <MessageCircle size={18} /> Démarrer sur WhatsApp
                </a>
                <p className="text-white/80 text-sm mt-6">
                  Zone d'intervention : Genève & région lémanique
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO CONTENT */}
      <SeoText>
        <h2 className="section-title mb-6">
          Consultant en automatisation IA <span className="accent-text">à Genève</span>
        </h2>
        <p className="body-text leading-relaxed mb-5">
          Je suis Christophe Battras, consultant en déploiement d'intelligence artificielle
          pour les PME suisses romandes. Depuis 12 ans, je dirige mes propres entreprises à
          Genève — un service de valet parking à l'aéroport et une activité de chauffeur VIP
          haut de gamme — et j'ai utilisé l'IA pour les rendre plus rentables sans embaucher
          davantage. Aujourd'hui, j'aide d'autres dirigeants de PME à faire pareil : récupérer
          des heures chaque semaine, ne plus rater un appel client, et automatiser les tâches
          répétitives qui plombent vos marges.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Mon approche est concrète : pas de jargon, pas de slides à rallonge, pas de
          promesses creuses. Je commence toujours par un{" "}
          <Link to="/audit-ia" className="accent-text font-semibold">audit IA gratuit</Link> de
          60 minutes pendant lequel j'identifie les trois processus de votre entreprise qui
          ont le meilleur retour sur investissement à automatiser. Vous repartez avec un
          rapport écrit, que vous décidiez de travailler avec moi ou non. C'est gratuit, sans
          engagement, et limité à 4 audits par mois pour préserver la qualité.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Mes spécialités couvrent tout ce qui touche aux interactions client et aux flux
          d'information internes : un{" "}
          <Link to="/services/receptionniste-ia" className="accent-text font-semibold">
            réceptionniste vocal IA
          </Link>{" "}
          qui répond à vos appels 24h/24 et qualifie vos prospects, un{" "}
          <Link to="/services/whatsapp-ia" className="accent-text font-semibold">
            chatbot WhatsApp
          </Link>{" "}
          qui prend en charge les questions courantes, des pipelines email automatisés qui
          trient et répondent à votre place, l'intégration entre vos outils existants (Google
          Workspace, Outlook, CRM, comptabilité), et des agents sur mesure pour des
          processus spécifiques à votre métier.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Tout ce que je déploie est hébergé en Suisse, conforme à la LPD et au FADP, et
          configuré sur vos outils actuels — vous n'avez rien à apprendre, rien à installer.
          La mise en production prend 30 jours maximum, garantis. Et puisque je suis
          entrepreneur moi-même, je sais ce que veut dire "urgent" : je réponds par WhatsApp,
          le soir et le week-end inclus, sans système de tickets.
        </p>
        <p className="body-text leading-relaxed">
          Que vous soyez à Genève, Lausanne, Nyon, Morges, ou ailleurs en Suisse romande,
          et que vous dirigiez une fiduciaire, un cabinet médical, une agence immobilière,
          un service de transport, une PME industrielle ou une activité indépendante,
          réservez votre audit gratuit dès aujourd'hui — la conversation suivante se fera
          naturellement.
        </p>
      </SeoText>
    </Layout>
  );
};

const Testimonial = ({ quote, author, role }) => (
  <div className="card-base fade-in">
    <p className="accent-text text-4xl font-serif leading-none mb-3">"</p>
    <p className="title-text font-medium leading-relaxed mb-4">{quote}</p>
    <p className="text-sm body-text">
      <span className="title-text font-bold">{author}</span> — {role}
    </p>
  </div>
);

const FeaturedProduct = ({ badge, title, accent, desc, badges, stats, link, process, alt, reality }) => {
  return (
    <section className={`px-6 py-20 ${alt ? "section-alt" : ""}`}>
      <div className="max-w-6xl mx-auto fade-in">
        <span className="pill-badge mb-5">{badge}</span>
        <h2 className="section-title mb-6">
          {title} <span className="accent-text">{accent}</span>
        </h2>
        <p className="body-text text-lg leading-relaxed max-w-2xl mb-6">{desc}</p>
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {badges.map((b, i) => (
              <span key={i} className="pill-badge">{b}</span>
            ))}
          </div>
        )}

        <div className="mb-12">
          <StatBlock stats={stats} />
        </div>

        {reality && (
          <div className="card-base mb-12 fade-in" style={{ background: "#1a2744" }}>
            <span className="pill-badge-dark mb-4">Réalité du marché</span>
            <h3 className="text-white font-extrabold text-2xl md:text-3xl mb-4 leading-tight">
              Le problème que vous <span className="accent-text">connaissez bien.</span>
            </h3>
            <p className="text-white/80 leading-relaxed">
              Un prospect qui n'obtient pas de réponse ne rappelle pas.{" "}
              <span className="accent-text font-bold">Il appelle votre concurrent.</span>
            </p>
          </div>
        )}

        <SectionHeader badge="Le processus" title="Trois étapes." accentTitle="Aucun effort." centered={false} />
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {process.map((p) => <ProcessCard key={p.num} {...p} />)}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <Link to="/contact" className="btn-primary" data-testid={`featured-demo-${link.replace(/\//g, "-")}`}>
            Demander une démo gratuite <ArrowRight size={18} />
          </Link>
          <Link to={link} className="btn-secondary" data-testid={`featured-learn-${link.replace(/\//g, "-")}`}>
            En savoir plus <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Home;
